<script lang="ts">
  import { session } from '../lib/session.svelte';
  import {
    decryptPayload, base64ToBytes,
    type PrivatePayload,
  } from '../lib/crypto';
  import { api } from '../lib/api';
  import { onSpaLink } from '../lib/navigate';
  import { renderMarkdown } from '../lib/markdown';
  import { privateTagIndex } from '../lib/tagIndex';
  import type { Activity } from '../../../shared/types';

  interface Props {
    activity: Activity;
    /** Pre-decrypted payload for private rows (passed in by Home to avoid
     *  double-decrypting once per render). Null for non-private rows. */
    privateCleartext?: PrivatePayload | null;
    onDeleted: (id: string) => void;
    onEdit?: (a: Activity) => void;
    /** Called when the row updates its own activity (e.g. after a heart
     *  toggle). The parent should patch its list so the change persists. */
    onChanged?: (a: Activity) => void;
  }
  let {
    activity, privateCleartext = null,
    onDeleted, onEdit, onChanged,
  }: Props = $props();

  // Fallback decrypt for the case where Home hasn't pre-computed yet (e.g.
  // immediately after a create) — we tolerate redundant work to keep the
  // contract simple.
  const decrypted: PrivatePayload | null = $derived.by(() => {
    if (activity.visibility !== 'private') return null;
    if (privateCleartext) return privateCleartext;
    if (!session.dek) return null;
    try {
      return decryptPayload(
        { ciphertext: base64ToBytes(activity.ciphertext), nonce: base64ToBytes(activity.nonce) },
        session.dek,
      );
    } catch {
      return null;
    }
  });

  function formatDate(epochSeconds: number | null | undefined): string {
    if (!epochSeconds) return '';
    const d = new Date(epochSeconds * 1000);
    return d.toLocaleString('nb-NO', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    });
  }

  // created_at is epoch *milliseconds* (Date.now() on the server). The
  // existing formatDate above takes seconds because scheduled_at is stored
  // that way. Date-only is enough for "added on" — time of day is noise.
  function formatDateOnly(epochMs: number): string {
    return new Date(epochMs).toLocaleDateString('nb-NO', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    });
  }

  /**
   * OpenStreetMap link for a location.
   *  - If coordinates are present → /?mlat=…&mlon=…&zoom=15 (shows a pin)
   *  - Else → /search?query=… (shows search results for the label)
   *
   * We deliberately build the URL with the WHATWG URL constructor so the query
   * string is correctly percent-encoded; manual concat would break on commas
   * or Norwegian characters in place names.
   */
  function osmLink(label: string, lat: number | null, lng: number | null): string {
    if (typeof lat === 'number' && typeof lng === 'number') {
      const u = new URL('https://www.openstreetmap.org/');
      u.searchParams.set('mlat', String(lat));
      u.searchParams.set('mlon', String(lng));
      u.hash = `map=15/${lat}/${lng}`;
      return u.toString();
    }
    const u = new URL('https://www.openstreetmap.org/search');
    u.searchParams.set('query', label);
    return u.toString();
  }

  // Authz mirrors server/activities.ts:
  //   - private: owner-only (server only returns yours anyway)
  //   - public:  owner if session.user.id === owner_id
  //   - semi:    owner if the server included owner_id in this row — the
  //              server only sends it to the owner, so its presence is the
  //              ownership signal. Anyone else sees no owner_id and no buttons.
  //   - moderators can delete (but not edit) any non-private row.
  const isOwner = $derived(
    activity.visibility === 'private'
      ? true
      : activity.visibility === 'public'
        ? session.user?.id === activity.owner_id
        : 'owner_id' in activity && activity.owner_id === session.user?.id,
  );
  const isModerator = $derived(session.user?.is_moderator === true);
  const canEdit = $derived(isOwner); // editing always requires ownership
  const canDelete = $derived(
    activity.visibility === 'private'
      ? true
      : isOwner || isModerator,
  );

  // Build a normalised "for-editing" copy so the form receives the canonical
  // Activity shape (the form decides whether the payload is private or not).
  function startEdit() {
    if (!onEdit) return;
    onEdit(activity);
  }

  async function del() {
    if (!confirm('Slett denne oppføringen?')) return;
    if (activity.visibility === 'private' && decrypted) {
      await privateTagIndex.release(decrypted.tags);
    }
    await api.deleteActivity(activity.id);
    onDeleted(activity.id);
  }

  // --- Hearts ----------------------------------------------------------------
  // Optimistic toggle via a local override that wins over the prop. On
  // success we both update the local override AND let the parent know, so
  // a re-render (parent's list refreshing) still shows the right state.
  let localOverride: Activity | null = $state(null);
  const view = $derived(localOverride ?? activity);
  let heartBusy = $state(false);

  async function toggleHeart() {
    if (!session.user || heartBusy) return;
    if (view.visibility === 'private') return;
    heartBusy = true;
    const wasHearted = view.viewer_hearted;
    const optimistic: Activity = {
      ...view,
      viewer_hearted: !wasHearted,
      heart_count: view.heart_count + (wasHearted ? -1 : 1),
    };
    localOverride = optimistic;
    try {
      const updated = wasHearted
        ? await api.unheartActivity(view.id)
        : await api.heartActivity(view.id);
      localOverride = updated;
      onChanged?.(updated);
    } catch {
      localOverride = null;
    } finally {
      heartBusy = false;
    }
  }

  // --- "Gjort" (completion mark) ------------------------------------------
  // Works on EVERY visibility the viewer can see. For private rows it acts
  // as a personal checkbox; for public/semi/friends it contributes to the
  // visible done_count statistic.
  let doneBusy = $state(false);
  async function toggleDone() {
    if (!session.user || doneBusy) return;
    doneBusy = true;
    const wasDone = view.viewer_done;
    localOverride = {
      ...view,
      viewer_done: !wasDone,
      done_count: view.done_count + (wasDone ? -1 : 1),
    };
    try {
      const updated = wasDone
        ? await api.undoneActivity(view.id)
        : await api.doneActivity(view.id);
      localOverride = updated;
      onChanged?.(updated);
    } catch {
      localOverride = null;
    } finally {
      doneBusy = false;
    }
  }

  // --- Archive / Hide -----------------------------------------------------
  // Two per-viewer flags. Archive applies to anyone (including the owner);
  // hide applies only to non-owners. Both opt the row out of the default
  // listing — the user has to toggle "Vis arkiv" / "Vis skjulte" to see
  // them again.
  let archiveBusy = $state(false);
  let hideBusy = $state(false);

  async function toggleArchive() {
    if (!session.user || archiveBusy) return;
    archiveBusy = true;
    const wasArchived = view.viewer_archived;
    localOverride = { ...view, viewer_archived: !wasArchived };
    try {
      const updated = wasArchived
        ? await api.unarchiveActivity(view.id)
        : await api.archiveActivity(view.id);
      localOverride = updated;
      onChanged?.(updated);
    } catch {
      localOverride = null;
    } finally {
      archiveBusy = false;
    }
  }

  async function toggleHide() {
    if (!session.user || hideBusy) return;
    hideBusy = true;
    const wasHidden = view.viewer_hidden;
    localOverride = { ...view, viewer_hidden: !wasHidden };
    try {
      const updated = wasHidden
        ? await api.unhideActivity(view.id)
        : await api.hideActivity(view.id);
      localOverride = updated;
      onChanged?.(updated);
    } catch {
      localOverride = null;
    } finally {
      hideBusy = false;
    }
  }

  // --- Bookmarks -----------------------------------------------------------
  let bookmarkBusy = $state(false);
  async function toggleBookmark() {
    if (!session.user || bookmarkBusy) return;
    if (view.visibility === 'private') return;
    bookmarkBusy = true;
    const wasBookmarked = view.viewer_bookmarked;
    localOverride = { ...view, viewer_bookmarked: !wasBookmarked };
    try {
      const updated = wasBookmarked
        ? await api.unbookmarkActivity(view.id)
        : await api.bookmarkActivity(view.id);
      localOverride = updated;
      onChanged?.(updated);
    } catch {
      localOverride = null;
    } finally {
      bookmarkBusy = false;
    }
  }

  /**
   * Share-link: copy /aktivitet/<id> (absolute URL) to the clipboard. Private rows
   * are shareable in principle but only the owner can decrypt — the receiver
   * sees a "private, can't view" message. We still show the button so the
   * owner can save the link.
   */
  let copiedAt: number | null = $state(null);
  async function copyPermalink() {
    const url = `${window.location.origin}/aktivitet/${activity.id}`;
    try {
      await navigator.clipboard.writeText(url);
      copiedAt = Date.now();
      setTimeout(() => { copiedAt = null; }, 1500);
    } catch {
      // Fallback: open a prompt so the user can copy manually.
      window.prompt('Kopier denne lenken:', url);
    }
  }
</script>

{#snippet locationLine(label: string, lat: number | null, lng: number | null)}
  <p class="muted">
    📍
    <a
      href={osmLink(label, lat, lng)}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Vis ${label} på OpenStreetMap`}
    >{label}</a>
  </p>
{/snippet}

<article class="card" aria-labelledby={`act-${activity.id}-h`}>
  {#if activity.visibility === 'private'}
    {#if decrypted}
      <h3 id={`act-${activity.id}-h`} style="display: flex; align-items: center;">
        <a href={`/aktivitet/${activity.id}`}
           onclick={(e) => onSpaLink(e, `/aktivitet/${activity.id}`)}
           style="color: inherit; text-decoration: none;">
          {decrypted.title}
        </a>
        <span class="vis-badge private">Privat</span>
        <!-- Bookmark marker — private rows never have one (always false), but
             keep the slot consistent so all rows look the same shape. -->
        {#if activity.viewer_bookmarked}
          <span class="vis-badge bookmarked" title="Bokmerket">★ Bokmerket</span>
        {/if}
      </h3>
      {#if decrypted.description}
        <div class="md" style="margin: 0.25rem 0;">{@html renderMarkdown(decrypted.description)}</div>
      {/if}
      {#if decrypted.tags.length}
        <div>
          {#each decrypted.tags as t}
            <a href={`/etiketter/${encodeURIComponent(t)}`}
               onclick={(e) => onSpaLink(e, `/etiketter/${encodeURIComponent(t)}`)}
               class="tag private"
               style="text-decoration: none; color: inherit;">{t}</a>
          {/each}
        </div>
      {/if}
      {#if decrypted.loc_label}
        {@render locationLine(decrypted.loc_label, null, null)}
      {/if}
      {#if decrypted.scheduled_at}<p class="muted">🕒 {formatDate(decrypted.scheduled_at)}</p>{/if}
      <p class="muted" style="font-size: 0.8rem;">Lagt til {formatDateOnly(activity.created_at)}</p>
    {:else if !session.dek}
      <h3 id={`act-${activity.id}-h`} style="display: flex; align-items: center;">
        <span class="vis-badge private">Privat</span>
      </h3>
      <p class="muted">
        Innholdet er kryptert. Lås opp øverst på siden for å vise det.
      </p>
    {:else}
      <p class="muted">Dekrypterer …</p>
    {/if}
  {:else}
    <h3 id={`act-${activity.id}-h`} style="display: flex; align-items: center;">
      <a href={`/aktivitet/${activity.id}`}
         onclick={(e) => onSpaLink(e, `/aktivitet/${activity.id}`)}
         style="color: inherit; text-decoration: none;">
        {activity.title}
      </a>
      <span class="vis-badge {activity.visibility}">
        {#if activity.visibility === 'semi'}
          Anonym
        {:else if activity.visibility === 'friends'}
          Venner
        {:else}
          Offentlig
        {/if}
      </span>
      {#if activity.viewer_bookmarked}
        <span class="vis-badge bookmarked" title="Bokmerket">★ Bokmerket</span>
      {/if}
    </h3>
    {#if activity.description}
      <div class="md" style="margin: 0.25rem 0;">{@html renderMarkdown(activity.description)}</div>
    {/if}
    {#if activity.tags.length}
      <div>
        {#each activity.tags as t}
          <a href={`/etiketter/${encodeURIComponent(t)}`}
             onclick={(e) => onSpaLink(e, `/etiketter/${encodeURIComponent(t)}`)}
             class="tag"
             style="text-decoration: none; color: inherit;">{t}</a>
        {/each}
      </div>
    {/if}
    {#if activity.loc_label}
      {@render locationLine(activity.loc_label, activity.loc_lat, activity.loc_lng)}
    {/if}
    {#if activity.scheduled_at}<p class="muted">🕒 {formatDate(activity.scheduled_at)}</p>{/if}
    <p class="muted" style="font-size: 0.8rem;">
      Lagt til {formatDateOnly(activity.created_at)}
      {#if activity.visibility === 'public' && activity.owner_display}
        av
        {#if activity.owner_username}
          <a href={`/${activity.owner_username}/liste`}
             onclick={(e) => onSpaLink(e, `/${activity.owner_username}/liste`)}>{activity.owner_display}</a>
        {:else}
          {activity.owner_display}
        {/if}
      {/if}
    </p>
  {/if}

  <div class="row" style="margin-top: 0.5rem;">
    {#if view.visibility !== 'private'}
      {#if session.user}
        <button
          type="button"
          onclick={toggleHeart}
          disabled={heartBusy}
          aria-pressed={view.viewer_hearted}
          aria-label={view.viewer_hearted ? 'Fjern hjerte' : 'Sett hjerte'}
          class={view.viewer_hearted ? 'primary' : ''}
        >
          {view.viewer_hearted ? '♥' : '♡'} {view.heart_count}
        </button>
        <button
          type="button"
          onclick={toggleBookmark}
          disabled={bookmarkBusy}
          aria-pressed={view.viewer_bookmarked}
          aria-label={view.viewer_bookmarked ? 'Fjern bokmerke' : 'Bokmerk'}
          title={view.viewer_bookmarked ? 'Fjern bokmerke' : 'Bokmerk'}
        >
          {view.viewer_bookmarked ? '★ Bokmerket' : '☆ Bokmerk'}
        </button>
      {:else if view.heart_count > 0}
        <span class="muted" aria-label="Antall hjerter">♡ {view.heart_count}</span>
      {/if}
    {/if}
    <!-- "Gjort" works on every visibility — including the owner's private
         rows where it's a personal todo checkbox. -->
    {#if session.user}
      <button
        type="button"
        onclick={toggleDone}
        disabled={doneBusy}
        aria-pressed={view.viewer_done}
        aria-label={view.viewer_done ? 'Marker som ikke gjort' : 'Marker som gjort'}
        title={view.viewer_done ? 'Du har gjort dette' : 'Dette har jeg gjort'}
        class={view.viewer_done ? 'primary' : ''}
      >
        {view.viewer_done ? '✓ Gjort' : '☐ Gjort'} {view.done_count > 0 ? view.done_count : ''}
      </button>
    {:else if view.visibility !== 'private' && view.done_count > 0}
      <span class="muted" aria-label="Antall som har gjort dette">✓ {view.done_count}</span>
    {/if}
    {#if canEdit && onEdit}
      <button type="button" onclick={startEdit}>Rediger</button>
    {/if}
    {#if session.user}
      <button
        type="button"
        onclick={toggleArchive}
        disabled={archiveBusy}
        aria-pressed={view.viewer_archived}
        title={view.viewer_archived ? 'Hent ut av arkivet' : 'Arkiver — gjem fra hovedlisten, behold for historikk'}
      >
        {view.viewer_archived ? '📦 Arkivert' : '📦 Arkiver'}
      </button>
    {/if}
    {#if session.user && !isOwner}
      <!-- Hide is non-owner only — owners can delete or archive instead. -->
      <button
        type="button"
        onclick={toggleHide}
        disabled={hideBusy}
        aria-pressed={view.viewer_hidden}
        title={view.viewer_hidden ? 'Vis igjen' : 'Gjem — denne appellerer ikke til meg'}
      >
        {view.viewer_hidden ? '🙈 Skjult' : '🙈 Gjem'}
      </button>
    {/if}
    {#if canDelete}
      <button class="danger" type="button" onclick={del}>Slett</button>
    {/if}
    <button type="button" onclick={copyPermalink}
            aria-label="Kopier lenke til denne aktiviteten">
      {copiedAt ? 'Kopiert!' : 'Del'}
    </button>
  </div>
</article>
